import { useCallback, useEffect, useRef } from 'react';

/**
 * Generic "this should have resolved by now" timer.
 *
 * Shared by the flows that latch `sharedState.loading` while waiting on something they
 * don't control — a recorded turn being transcribed, a realtime session coming up. Each
 * has its own budget and its own recovery, but the mechanics are identical: arm when the
 * wait starts, stand down the moment it resolves, fire exactly once if it never does,
 * and never outlive the component.
 *
 * Armed explicitly by the caller rather than by watching `active`, so it only ever guards
 * the wait it was written for and not the other flows sharing the same flag.
 *
 * @param {object} params
 * @param {boolean} params.active - The wait is still in flight; falling false disarms.
 * @param {number} params.timeoutMs - How long the wait is allowed to take.
 * @param {(stuckForMs: number) => void} params.onStuck - Called once when it never resolved.
 * @returns {() => void} arm - Call when the wait begins.
 */
export default function useStuckWatchdog({ active, timeoutMs, onStuck }) {
  const timerRef = useRef(null);
  const onStuckRef = useRef(onStuck);

  // Keep the callback current without re-arming the timer on every render.
  useEffect(() => {
    onStuckRef.current = onStuck;
  }, [onStuck]);

  const disarm = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const arm = useCallback(() => {
    disarm();
    const armedAt = Date.now();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onStuckRef.current?.(Date.now() - armedAt);
    }, timeoutMs);
  }, [disarm, timeoutMs]);

  // The wait resolved — successfully or via a handled error. Stand down.
  useEffect(() => {
    if (!active) disarm();
  }, [active, disarm]);

  // Never outlive the component.
  useEffect(() => disarm, [disarm]);

  return arm;
}
